import { isDefined, isEmpty, isNotDefined, isNotEmpty, type TeamData, type TeamId } from '@terros/common'
import { type FocusRow, type TeamFocusLayout } from '../types'
import {
  capTeamsForRow,
  compareTeamsForRow,
  getAncestorTeams,
  getDirectChildTeams,
  getParentTeam,
  getPeerTeams,
  toTeamRelations,
  type TeamRelations,
} from './sortTeams'

export const EMPTY_FOCUS_LAYOUT: TeamFocusLayout = {
  focusTeam: undefined,
  rows: [],
  layoutKey: '',
  drawnTeamIds: new Set(),
  hiddenChildCounts: new Map(),
}

export type FocusLayoutInput = {
  teams: TeamData[]
  focusTeamId: TeamId | null
  expandedRowIds: string[]
}

const ROOT_COHORT = 'roots'

export const toCohortRowId = (parentTeamId: TeamId | undefined): string => `cohort:${parentTeamId ?? ROOT_COHORT}`

export const buildFocusLayout = ({ teams, focusTeamId, expandedRowIds }: FocusLayoutInput): TeamFocusLayout => {
  if (isEmpty(teams)) return EMPTY_FOCUS_LAYOUT

  const relations = toTeamRelations(teams)
  const focusTeam = resolveFocusTeam(focusTeamId, relations)
  if (isNotDefined(focusTeam)) return EMPTY_FOCUS_LAYOUT

  const expanded = new Set(expandedRowIds)
  const ancestorRows = getAncestorTeams(focusTeam, relations).map((ancestor) =>
    toPeerRow(ancestor, 'ancestor', relations, expanded)
  )
  const rows = [...ancestorRows, toPeerRow(focusTeam, 'focus', relations, expanded)]
  const childRow = toChildRow(focusTeam, relations, expanded)
  if (isNotEmpty(childRow.teams)) rows.push(childRow)

  const drawnTeamIds = new Set(rows.flatMap((row) => row.teams.map((team) => team.teamId)))

  return {
    focusTeam,
    rows,
    layoutKey: rows.map(toRowKey).join('|'),
    drawnTeamIds,
    hiddenChildCounts: toHiddenChildCounts({ rows, drawnTeamIds, focusTeam, relations }),
  }
}

const toRowKey = (row: FocusRow): string =>
  [row.rowId, row.teams.map((team) => team.teamId).join(','), row.overflowCount, row.isExpanded].join('#')

const resolveFocusTeam = (focusTeamId: TeamId | null, relations: TeamRelations<TeamData>): TeamData | undefined => {
  const focusTeam = isDefined(focusTeamId) ? relations.teamsById.get(focusTeamId) : undefined
  if (isDefined(focusTeam)) return focusTeam
  return [...relations.rootTeams].sort(compareTeamsForRow(relations.directChildMap))[0]
}

const toPeerRow = (
  team: TeamData,
  kind: 'ancestor' | 'focus',
  relations: TeamRelations<TeamData>,
  expanded: Set<string>
): FocusRow => {
  const rowId = toCohortRowId(getParentTeam(team, relations.teamsById)?.teamId)
  const isExpanded = expanded.has(rowId)
  const { visible, overflowCount } = capTeamsForRow({
    teams: getPeerTeams(team, relations),
    directChildMap: relations.directChildMap,
    pinnedTeamId: team.teamId,
    expanded: isExpanded,
  })

  return { rowId, kind, teams: visible, overflowCount, isExpanded }
}

const toChildRow = (focusTeam: TeamData, relations: TeamRelations<TeamData>, expanded: Set<string>): FocusRow => {
  const rowId = toCohortRowId(focusTeam.teamId)
  const isExpanded = expanded.has(rowId)
  const { visible, overflowCount } = capTeamsForRow({
    teams: getDirectChildTeams(focusTeam, relations),
    directChildMap: relations.directChildMap,
    expanded: isExpanded,
  })

  return { rowId, kind: 'children', teams: visible, overflowCount, isExpanded }
}

type HiddenChildCountsInput = {
  rows: FocusRow[]
  drawnTeamIds: Set<TeamId>
  focusTeam: TeamData
  relations: TeamRelations<TeamData>
}

type HiddenChildCounts = Map<TeamId, number>

const toHiddenChildCounts = ({
  rows,
  drawnTeamIds,
  focusTeam,
  relations,
}: HiddenChildCountsInput): HiddenChildCounts => {
  const counts = new Map<TeamId, number>()

  for (const row of rows) {
    for (const team of row.teams) {
      if (team.teamId === focusTeam.teamId) continue
      const hidden = getDirectChildTeams(team, relations).filter((child) => !drawnTeamIds.has(child.teamId)).length
      if (hidden > 0) counts.set(team.teamId, hidden)
    }
  }

  return counts
}
